import React, { useState, useCallback, useRef, useEffect } from "react";
import { Spinner } from "../../Common";
import Button from "../../Common/Button";
import styles from "./LientransferfLow.module.css";
import singleBackArrow from "../../public/images/singleBackArrow.svg";
import {
  TransferFee,
  WalletConversion,
  mcbGlobalAccount,
  mcbPartnerAccount,
  WalletLienConversion,
  mcbWallets,
  mcbPartnerWallets,
} from "../../api";
import { reFormatDate, maskAccountNo } from "./../../Constants/helper";
import {
  AudFlag,
  Bank,
  BGPFlag,
  CadFlag,
  Calender,
  Dollar,
  EuroFlag,
  IndiaFlag,
  Refresh,
  SGDFlag,
  SuccessVideo,
  USFlag,
  wallet,
  info,
} from "../../public/images/icon";

const CONVERSION_STEPS = { DETAILS: 1, SUMMARY: 2, CONFIRMATION: 3 };

const CURRENCY_FLAG_MAP = {
  USD: USFlag,
  GBP: BGPFlag,
  EUR: EuroFlag,
  SGD: SGDFlag,
  CAD: CadFlag,
  AUD: AudFlag,
  INR: IndiaFlag,
};

const CurrencyFlag = ({ code, size = 20 }) => {
  const FlagSrc = CURRENCY_FLAG_MAP[code];
  if (!FlagSrc) {
    return (
      <img
        src={wallet}
        alt={code}
        width={size}
        height={size}
        style={{ objectFit: "contain" }}
      />
    );
  }
  return (
    <img
      src={FlagSrc}
      alt={code}
      width={size}
      height={size}
      style={{ objectFit: "contain" }}
    />
  );
};

const ConversionAccountCard = ({ accountData }) => {
  return (
    <div className={styles.beneCard}>
      <div className={styles.beneCardInfo}>
        <p className={styles.beneCardName}>{accountData?.user?.name || "--"}</p>
        <p className={styles.beneCardSub}>{maskAccountNo(accountData?.vendorAccountNo) || "--"}</p>
      </div>
    </div>
  );
};

const InfoTooltip = ({ text }) => {
  const [show, setShow] = useState(false);
  return (
    <span
      className={styles.tooltip}
      onMouseEnter={() => setShow(true)}
      onMouseLeave={() => setShow(false)}
      style={{ display: "inline-flex", alignItems: "center", marginLeft: 4 }}
    >
      <img
        src={info}
        alt="info"
        width={14}
        height={14}
        style={{ cursor: "pointer" }}
      />
      {show && <span className={styles.tooltipBox}>{text}</span>}
    </span>
  );
};

const useDebounce = (fn, delay) => {
  const timerRef = useRef(null);
  const debounced = useCallback(
    (...args) => {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => fn(...args), delay);
    },
    [fn, delay],
  );
  debounced.cancel = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);
  return debounced;
};

const CurrencyDropdown = ({
  options = [],
  value,
  onChange,
  disabled,
  className = "",
  disableZeroBalance = true,
}) => {
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef(null);
  const selected = options.find((opt) => opt.vendorCurrency === value);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div
      ref={dropdownRef}
      className={`${styles.currencyDropdown} ${className}`}
    >
      <div
        className={styles.currencySelected}
        onClick={() => !disabled && setOpen((prev) => !prev)}
      >
        <CurrencyFlag code={selected?.vendorCurrency} size={18} />
        <span style={{ marginLeft: 8 }}>{selected?.vendorCurrency}</span>
        <span className={styles.dropdownArrow}>▾</span>
      </div>
      {open && (
        <div className={styles.currencyMenu}>
          {options.map((opt) => {
            const hasBalance = !disableZeroBalance || parseFloat(opt.balance || 0) > 0;            return (
            <div
              key={opt.vendorCurrency}
             className={`${styles.currencyOption} ${
                opt.vendorCurrency === value
                    ? styles.currencyOptionActive
                    : ""
                } ${!hasBalance ? styles.currencyOptionDisabled : ""}`}
              onClick={() => {
                  if (!hasBalance) return;

                onChange(opt.vendorCurrency);
                setOpen(false);
              }}
                style={{
                  opacity: hasBalance ? 1 : 0.4,
                  cursor: hasBalance ? "pointer" : "not-allowed",
                }}
            >
              <CurrencyFlag code={opt.vendorCurrency} size={18} />

              <span style={{ marginLeft: 8 }}>{opt.vendorCurrency}</span>
            </div>
          );
          })}
        </div>
      )}
    </div>
  );
};

/* ---------------- STEP 1 : Amount + Currency ---------------- */

const ConversionDetailsStep = ({
  userId,
  accountData,
  onNext,
  onClose,
  initialData,
  apiType,
}) => {
  const [walletAccounts, setWalletAccounts] = useState([]);
  const [walletLoading, setWalletLoading] = useState(true);
  const [fromCurrency, setFromCurrency] = useState(null);
  const [toCurrency, setToCurrency] = useState(null);
  const [fromAmount, setFromAmount] = useState("");
  const [toAmount, setToAmount] = useState("");
  const [fromAmountError, setFromAmountError] = useState("");
  const [charges, setCharges] = useState({ totalFees: "-", rate: 0 });
  const [feeLoading, setFeeLoading] = useState(false);
  const [activeInput, setActiveInput] = useState(null);
  const [showConversionFee, setShowConversionFee] = useState(false);
  const isInitializingRef = useRef(false);
  const [toAmountError, setToAmountError] = useState("");
  const MIN_AMOUNT = 2;

const fromCurrencyOptions = walletAccounts;

const toCurrencyOptions = walletAccounts.filter(
  (item) => item.vendorCurrency !== fromCurrency?.vendorCurrency
);

  useEffect(() => {
    const fetch = async () => {
      setWalletLoading(true);
      try {
        const res =
          apiType === "partner"
            ? await mcbPartnerWallets(userId)
            : await mcbWallets(userId);
        const accounts = res?.data?.data?.accounts || [];
        setWalletAccounts(accounts);

        if (initialData) {
          isInitializingRef.current = true;
          setFromAmount(String(initialData.fromAmount || ""));
          setToAmount(String(initialData.toAmount || ""));
          setFromCurrency(initialData.fromCurrency);
          setToCurrency(initialData.toCurrency);
          setCharges(initialData.charges);
          setTimeout(() => (isInitializingRef.current = false), 0);
        } else {
          const first =
            accounts.find((a) => parseFloat(a.balance) > 0) || accounts[0];
          const second =
            accounts.find((a) => a.vendorCurrency !== first?.vendorCurrency) ||
            accounts[1];
          setFromCurrency(first);
          setToCurrency(second);
        }
      } catch (err) {
        console.error("Error fetching wallet:", err);
      } finally {
        setWalletLoading(false);
      }
    };
    fetch();
  }, [userId]);

  const fetchConversion = useCallback(
    async (payload) => {
      if (!payload.fromCurrency || !payload.toCurrency) return;
      setFeeLoading(true);
      try {
        const res = await TransferFee(
          payload.buyAmount,
          payload.sellAmount,
          payload.toCurrency,
          payload.fromCurrency,
          "LOCAL",
          "CONVERSION",
          accountData?.user?.countryCode,
          userId,
        );
        const data = res?.data?.data || res?.data || {};
        setCharges({ totalFees: data.totalFees ?? "-", rate: data.rate ?? 0 });

        const isInitialRateCall =
          payload.sellAmount === 1 &&
          payload.buyAmount === undefined &&
          !fromAmount &&
          !toAmount;
        setShowConversionFee(!isInitialRateCall);

        if (!isInitialRateCall) {
          let calculatedFrom;
          let calculatedTo;
          if (payload.sellAmount !== undefined) {
            calculatedTo = data.toAmount != null ? String(data.toAmount) : "";
            setToAmount(calculatedTo);
            calculatedFrom = payload.sellAmount;
          } else {
            calculatedFrom = data.fromAmount != null ? String(data.fromAmount) : "";
            setFromAmount(calculatedFrom);
            calculatedTo = payload.buyAmount;
          }

          const avail = parseFloat(
            fromCurrency?.balance - (fromCurrency?.holdBalance || 0),
          ).toFixed(2);

          const fromValid = validateFromAmount(calculatedFrom, avail);
          const fromNum = parseFloat(calculatedFrom);
          const toNum = parseFloat(calculatedTo);

          if (fromValid && !isNaN(fromNum) && fromNum < MIN_AMOUNT) {
            setFromAmountError(
              `Amount must be greater than 2`,
            );
          }

          if (!isNaN(toNum) && toNum < MIN_AMOUNT) {
            setToAmountError(
              `Amount must be greater than 2`,
            );
          } else {
            setToAmountError("");
          }
        }
      } catch (err) {
        console.error("TransferFee error:", err);
      } finally {
        setFeeLoading(false);
        setActiveInput(null);
      }
    },
    [fromCurrency, userId, fromAmount, toAmount],
  );

  const debouncedFetch = useDebounce(fetchConversion, 1200);

  useEffect(() => {
    if (isInitializingRef.current) return;
    if (!fromCurrency?.vendorCurrency || !toCurrency?.vendorCurrency) return;
    if (fromAmountError || toAmountError) return;
    if (activeInput === "FROM" && fromAmount) {
      debouncedFetch({
        fromCurrency: fromCurrency.vendorCurrency,
        toCurrency: toCurrency.vendorCurrency,
        sellAmount: fromAmount,
      });
    }
    if (activeInput === "TO" && toAmount) {
      debouncedFetch({
        fromCurrency: fromCurrency.vendorCurrency,
        toCurrency: toCurrency.vendorCurrency,
        buyAmount: toAmount,
      });
    }
  }, [
    fromCurrency?.vendorCurrency,
    toCurrency?.vendorCurrency,
    fromAmount,
    toAmount,
    activeInput,
    fromAmountError,
    toAmountError
  ]);
  useEffect(() => {
    if (
      !fromCurrency?.vendorCurrency ||
      !toCurrency?.vendorCurrency ||
      fromAmount ||
      toAmount
    ) {
      return;
    }

    fetchConversion({
      sellAmount: 1,
      fromCurrency: fromCurrency.vendorCurrency,
      toCurrency: toCurrency.vendorCurrency,
    });
  }, [
    fromCurrency?.vendorCurrency,
    toCurrency?.vendorCurrency,
    accountData?.user?.countryCode,
  ]);

  const availableBalance = fromCurrency
    ? parseFloat(
        fromCurrency.balance - (fromCurrency.holdBalance || 0),
      ).toFixed(2)
    : "0.00";

  const validateFromAmount = (val, balance) => {
    const parsed = parseFloat(val) || 0;
    if (!val || parsed <= 0) {
      setFromAmountError("Please enter a valid amount.");
      debouncedFetch.cancel();
      return false;
    }
    if (parsed > parseFloat(balance)) {
      setFromAmountError(
        `Amount cannot be greater than available balance (${balance} ${fromCurrency?.vendorCurrency}).`,
      );
      debouncedFetch.cancel();
      setToAmount("");
      return false;
    }
    setFromAmountError("");
    return true;
  };

const handleFromAmountChange = (e) => {
  const val = e.target.value;
  const numericValue = parseFloat(val);
  setActiveInput("FROM");
  setFromAmountError("");
  setToAmountError("");

  if (!val) {
    debouncedFetch.cancel();
    setFromAmount("");
    setToAmount("");
    return;
  }
    if (!isNaN(numericValue) && numericValue < MIN_AMOUNT) {
      debouncedFetch.cancel();
      setFromAmount(val);
      setToAmount("");
      setFromAmountError(`Amount must be greater than 2`);
      return;
    }

  setFromAmount(val);
  validateFromAmount(val, availableBalance);
};

const handleToAmountChange = (e) => {
  const val = e.target.value;
  const numericValue = parseFloat(val);
  setActiveInput("TO");
  setFromAmountError("");
  setToAmountError("");

  if (!val) {
    debouncedFetch.cancel();
    setFromAmount("");
    setToAmount("");
    return;
  }
    if (!isNaN(numericValue) && numericValue < MIN_AMOUNT) {
      debouncedFetch.cancel();
      setToAmount(val);
      setFromAmount("");
      setToAmountError(`Amount must be greater than 2`);
      return;
    }

  setToAmount(val);
};

const handleFromCurrencyChange = (val) => {
  const selectedFrom = walletAccounts.find(
    (a) => a.vendorCurrency === val
  );

  if (!selectedFrom) return;

  setFromCurrency(selectedFrom);

  // If From and To become the same, automatically switch To
  if (val === toCurrency?.vendorCurrency) {
    const alternate = walletAccounts.find(
      (a) => a.vendorCurrency !== val
    );

    if (alternate) {
      setToCurrency(alternate);
    }
  }

  setFromAmountError("");
  setActiveInput("FROM");
  setShowConversionFee(false);
};

const handleToCurrencyChange = (val) => {
  const acc = walletAccounts.find(
    (a) => a.vendorCurrency === val
  );

  if (!acc) return;

  setToCurrency(acc);
  setActiveInput("FROM");
  setShowConversionFee(false);
};

  const handleNext = () => {
    if (!fromAmount || !toAmount) {
      setFromAmountError("Please enter an amount.");
      return;
    }
    if (fromAmountError || toAmountError) return;
    onNext({ fromAmount, toAmount, fromCurrency, toCurrency, charges });
  };

  if (walletLoading) {
    return (
      <div className={styles.centered}>
        <Spinner animation="border" size="sm" />
      </div>
    );
  }

  return (
    <div className={styles.stepContainer}>
      {feeLoading && (
        <div className={styles.fullScreenOverlay}>
          <Spinner animation="border" />
        </div>
      )}

      <ConversionAccountCard accountData={accountData} />

      <div className={styles.amountSection}>
        <p className={styles.amountLabel}>From</p>
        <div className={styles.amountRow}>
          <input
            type="number"
            className={styles.amountInput}
            value={fromAmount}
            onChange={handleFromAmountChange}
            disabled={feeLoading}
            onWheel={(e) => e.target.blur()}
          />
          <CurrencyDropdown
            options={fromCurrencyOptions}
            value={fromCurrency?.vendorCurrency}
            onChange={handleFromCurrencyChange}
            disabled={feeLoading}
            className={styles.currencyInsideInput}
          />
        </div>
        {fromAmountError && (
          <p className={styles.errorText}>{fromAmountError}</p>
        )}
        <p className={styles.availableText}>
          Available Amount: {availableBalance} {fromCurrency?.vendorCurrency}
        </p>
      </div>

      <div className={styles.conversionBlock}>
        <div className={styles.conversionLine} />
        <div className={styles.conversionRow}>
          <span className={styles.convIcon}>
            <img src={Refresh} alt="rate" width={18} height={18} />
          </span>
          <div className={styles.convContent}>
            <p className={styles.convLabel}>
              Conversion Rate
              <span className={styles.convValue}>
                {` 1 ${fromCurrency?.vendorCurrency} = ${charges?.rate ?? "-"} ${toCurrency?.vendorCurrency}`}
              </span>
              <InfoTooltip text="Actual rate may vary at time of conversion." />
            </p>
          </div>
        </div>
        <div className={styles.conversionRow}>
          <span className={styles.convIcon}>
            <img src={Dollar} alt="fee" width={18} height={18} />
          </span>
          <div className={styles.convContent}>
            <p className={styles.convLabel}>
              Conversion Fee:
              <span className={styles.convValue}>
                {!showConversionFee
                  ? " -"
                  : charges.totalFees === "-" || !charges.totalFees
                  ? " -"
                  : parseFloat(charges.totalFees) === 0
                    ? " Free"
                    : ` ${charges.totalFees} ${fromCurrency?.vendorCurrency}`}
              </span>
            </p>
          </div>
        </div>
      </div>

      <div className={styles.amountSection}>
        <p className={styles.amountLabel}>To</p>
        <div className={styles.amountRow}>
          <input
            type="number"
            className={styles.amountInput}
            value={toAmount}
            onChange={handleToAmountChange}
            disabled={feeLoading}
            onWheel={(e) => e.target.blur()}
          />
          <CurrencyDropdown
            options={toCurrencyOptions}
            value={toCurrency?.vendorCurrency}
            onChange={handleToCurrencyChange}
            disabled={feeLoading}
            disableZeroBalance={false}
            className={styles.currencyInsideInput}
          />
        </div>
          {toAmountError && (
            <p className={styles.errorText}>{toAmountError}</p>
          )}
      </div>

      <div className={styles.footerRow}>
        <Button onClick={onClose} title="Cancel" />
        <Button
          onClick={handleNext}
          title="Continue"
          disabled={feeLoading || !!fromAmountError || !!toAmountError}
          loading={feeLoading}
        />
      </div>
    </div>
  );
};

/* ---------------- STEP 2 : Summary ---------------- */

const ConversionSummaryStep = ({
  conversionData,
  accountData,
  onBack,
  onConfirm,
  onClose,
  submitting,
}) => {
  const { fromAmount, toAmount, fromCurrency, toCurrency, charges } =
    conversionData;
  const availableBalance = fromCurrency
    ? parseFloat(
        fromCurrency.balance - (fromCurrency.holdBalance || 0),
      ).toFixed(2)
    : "0.00";

  return (
      <div className={styles.stepContainer}>
      <div className={styles.backpayment}>
        <button
          type="button"
          className={styles.backButton}
          onClick={onBack}
          aria-label="Go back"
        >
          <img src={singleBackArrow} alt="Back" className={styles.backIcon} />
        </button>
        <p className={styles.stepTitle}>Conversion Summary</p>
      </div>
      <ConversionAccountCard accountData={accountData} />

      <div className={styles.conversionBlock} style={{ marginTop: 16 }}>
        <div className={styles.conversionLine} />

        <div className={styles.conversionRow}>
          <span className={styles.convIcon}>
            <img src={wallet} alt="from" width={18} height={18} />
          </span>
          <div className={styles.convContent}>
            <p className={styles.convLabel}>From Amount</p>
            <p
              className={styles.convValue}
              style={{ fontSize: 16, fontWeight: 600 }}
            >
              {fromAmount} {fromCurrency?.vendorCurrency}{" "}
              <CurrencyFlag code={fromCurrency?.vendorCurrency} size={18} />
            </p>
            <p className={styles.accountBalance}>
              Account Balance: {availableBalance} {fromCurrency?.vendorCurrency}
            </p>
          </div>
        </div>

        <div className={styles.conversionRow}>
          <span className={styles.convIcon}>
            <img src={Calender} alt="date" width={18} height={18} />
          </span>
          <div className={styles.convContent}>
            <p className={styles.convLabel}>
              Transfer On
              <span
                className={styles.convValue}
              >{` ${reFormatDate(new Date())}`}</span>
            </p>
          </div>
        </div>

        <div className={styles.conversionRow}>
          <span className={styles.convIcon}>
            <img src={Refresh} alt="rate" width={18} height={18} />
          </span>
          <div className={styles.convContent}>
            <p className={styles.convLabel}>
              Conversion Rate
              <span className={styles.convValue}>
                {` 1 ${fromCurrency?.vendorCurrency} = ${charges?.rate ?? "-"} ${toCurrency?.vendorCurrency}`}
              </span>
              <InfoTooltip text="Actual rate may vary at time of conversion." />
            </p>
          </div>
        </div>

        <div className={styles.conversionRow}>
          <span className={styles.convIcon}>
            <img src={Dollar} alt="fee" width={18} height={18} />
          </span>
          <div className={styles.convContent}>
            <p className={styles.convLabel}>
              Conversion Fee:
              <span className={styles.convValue}>
                {charges.totalFees === "-" || !charges.totalFees
                  ? " -"
                  : parseFloat(charges.totalFees) === 0
                    ? " Free"
                    : ` ${charges.totalFees} ${fromCurrency?.vendorCurrency}`}
              </span>
            </p>
          </div>
        </div>

        <div className={styles.conversionRow}>
          <span className={styles.convIcon}>
            <img src={Bank} alt="to" width={18} height={18} />
          </span>
          <div className={styles.convContent}>
            <p className={styles.convLabel}>To Amount</p>
            <p
              className={styles.convValue}
              style={{ fontSize: 16, fontWeight: 600 }}
            >
              {toAmount} {toCurrency?.vendorCurrency}{" "}
              <CurrencyFlag code={toCurrency?.vendorCurrency} size={18} />
            </p>
          </div>
        </div>
      </div>

      <div className={styles.footerRow}>
        <Button onClick={onClose} title="Cancel" disabled={submitting} />
        <Button
          onClick={onConfirm}
          title="Confirm Conversion"
          loading={submitting}
          disabled={submitting}
        />
      </div>
    </div>
  );
};

/* ---------------- STEP 3 : Confirmation ---------------- */

const ConversionConfirmationStep = ({ result, onClose }) => {
  if (!result?.success) {
    return (
      <div className={styles.confirmationContainer}>
        <p className={styles.confirmationTitle}>Conversion Failed</p>
        <p className={styles.confirmationSub}>
          {result?.message || "Something went wrong. Please try again."}
        </p>
        <div style={{ marginTop: 24 }}>
          <Button onClick={onClose} title="Back to Home" />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.confirmationContainer}>
      <div className={styles.confirmationIcon}>
        <img
          src={SuccessVideo}
          alt="success"
          width={70}
          height={70}
          style={{ borderRadius: "50%" }}
        />
      </div>

      <p className={styles.confirmationTitle}>
        Your conversion has been initiated
      </p>
      <p className={styles.confirmationSub}>
        Transfer ID : {result.transactionNo}
      </p>

      <div className={styles.summaryCard} style={{ marginTop: 20 }}>
        <div className={styles.summaryRow}>
          <span className={styles.summaryKey}>You will receive</span>
          <span className={styles.summaryVal}>
            {result.toAmount} {result.toCurrency}
          </span>
        </div>
        <div className={styles.summaryRow}>
          <span className={styles.summaryKey}>Currency Conversion</span>
          <span className={styles.summaryVal}>
            {result.fromCurrency} to {result.toCurrency}
          </span>
        </div>
        <div className={styles.summaryRow}>
          <span className={styles.summaryKey}>Exchange Rate</span>
          <span className={styles.summaryVal}>
            1 {result.fromCurrency} = {result.rate} {result.toCurrency}
          </span>
        </div>
        <div className={styles.summaryRow}>
          <span className={styles.summaryKey}>Date</span>
          <span className={styles.summaryVal}>
            {reFormatDate(result.createdAt || new Date())}
          </span>
        </div>
        <div className={styles.summaryRow}>
          <span className={styles.summaryKey}>Transfer Fee</span>
          <span className={styles.summaryVal}>
            {!result.fees || parseFloat(result.fees) === 0
              ? "Free"
              : `${result.fees} ${result.fromCurrency}`}
          </span>
        </div>
      </div>

      <div style={{ marginTop: 20 }}>
        <Button onClick={onClose} title="Back to Home" />
      </div>
    </div>
  );
};


const ConversionFlow = ({ userId, onClose, apiType, detail }) => {
  const [step, setStep] = useState(CONVERSION_STEPS.DETAILS);
  const [conversionData, setConversionData] = useState(null);
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [accountData, setAccountData] = useState(null);

  useEffect(() => {
    const fetchAccount = async () => {
      try {
        const res =
          apiType === "partner"
            ? await mcbPartnerAccount(userId)
            : await mcbGlobalAccount(userId);

        if (res?.status === 200) {
          const data = res.data?.data || null;

          if (apiType === "partner" && data) {
            setAccountData({
              ...data,
              user: {
                name: detail?.name,
                countryCode: detail?.countryCode,
              },
            });
          } else {
            setAccountData(data);
          }
        }
      } catch (err) {
        console.error("Error fetching global account:", err);
      }
    };
    if (userId) fetchAccount();
  }, [userId, apiType, detail]);

  const handleDetailsNext = (data) => {
    setConversionData(data);
    setStep(CONVERSION_STEPS.SUMMARY);
  };

  const handleConfirm = async () => {
    if (!conversionData) return;
    setSubmitting(true);
    try {
      const { toAmount, fromCurrency, toCurrency, charges } = conversionData;

      const payload = {
        buyAmount: parseFloat(toAmount),
        toCurrency: toCurrency?.vendorCurrency,
        fromCurrency: fromCurrency?.vendorCurrency,
        transactionType: "CONVERSION",
        reason: "converting currency",
        term_agreement: true,
      };

      const res =
        apiType === "partner"
          ? await WalletLienConversion(userId, payload)
          : await WalletConversion(userId, payload);
      const resData = res?.data;

      if (res?.status === 200 || resData?.status === 200) {
        const data = resData?.data || {};
        setResult({
          success: true,
          transactionNo: data.transactionNo,
          fromCurrency: data.fromCurrency || fromCurrency?.vendorCurrency,
          toCurrency: data.toCurrency || toCurrency?.vendorCurrency,
          toAmount: data.amount ?? toAmount,
          debitAmount: data.debitAmount,
          rate: data.forexRate ?? charges?.rate,
          fees: data.fees ?? charges?.totalFees,
          createdAt: data.createdAt,
        });
      } else {
        setResult({
          success: false,
          message:
            resData?.message ||
            (Array.isArray(resData?.error)
              ? resData.error.join(", ")
              : "Conversion failed. Please try again."),
        });
      }
      setStep(CONVERSION_STEPS.CONFIRMATION);
    } catch (err) {
      setResult({
        success: false,
        message:
          err?.response?.data?.message ||
          "Something went wrong. Please try again.",
      });
      setStep(CONVERSION_STEPS.CONFIRMATION);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={`${styles.flowRoot} ${styles.conversionFlow}`}>
      {step === CONVERSION_STEPS.DETAILS && (
        <ConversionDetailsStep
          userId={userId}
          accountData={accountData}
          onNext={handleDetailsNext}
          onClose={onClose}
          initialData={conversionData}
          apiType={apiType}
        />
      )}
      {step === CONVERSION_STEPS.SUMMARY && (
        <ConversionSummaryStep
          conversionData={conversionData}
          accountData={accountData}
          onBack={() => setStep(CONVERSION_STEPS.DETAILS)}
          onConfirm={handleConfirm}
          onClose={onClose}
          submitting={submitting}
        />
      )}
      {step === CONVERSION_STEPS.CONFIRMATION && (
        <ConversionConfirmationStep result={result} onClose={onClose} />
      )}
    </div>
  );
};

export default ConversionFlow;
